import React from "react";
import styled from "styled-components";
import { DEFAULT_TILE_WIDTH } from "../Tile/Tile";
import Stock from "../Stock";
import Waste from "../Waste";
import Foundation from "../Foundation";
import TableauStack from "../TableauStack";
import { PILE } from "../../engine/solitaire";

const VERTICAL_GAP = 12;
const HORIZONTAL_GAP = 0;

// Board owns the two-row, seven-column grid. Cells pin children to specific
// grid columns/rows so the child components stay layout-agnostic: Stock,
// Waste, Foundation, and TableauStack render their own cards without
// knowing where they sit on the board.
//
// Top row layout:
//   col 1 stock
//   col 2 waste (fan overflows into col 3 which is left empty as a gutter)
//   cols 4..7 foundations 0..3
// Bottom row: cols 1..7 map to tableau columns 0..6.
//
// Click handlers funnel upward — Board doesn't know about engine moves,
// it just translates "clicked this visual thing" into "source X was clicked."
// Solitaire does the engine-level resolution.
function Board({
  state,
  selection,
  cardBack,
  // True only for the player's own board: enables drop-target
  // attributes. Card source tags render on EVERY board — spectated
  // windows hit-test remote presses against them (they stay inert
  // there; SolitaireCore turns pointer-events off).
  interactive = false,
  wasteVisibleCount,
  stockDeadEnd,
  onDrawClick,
  onWasteClick,
  onFoundationClick,
  onTableauCardClick,
  onTableauEmptyClick,
  foundationCellRefs,
}) {
  const isSelected = (pile, index) =>
    selection != null && selection.pile === pile && selection.index === index;
  const wasteSelected = selection != null && selection.pile === PILE.WASTE;

  return (
    // data-felt: the green playing surface. Both drag adapters clamp
    // and clip the in-flight ghost to this rect — cards live on the
    // felt, not the chrome.
    <Grid data-felt="true">
      <Cell $col={1} $row={1}>
        <Stock
          count={state.stock.length}
          canRecycle={state.waste.length > 0}
          deadEnd={stockDeadEnd}
          cardBack={cardBack}
          onClick={onDrawClick}
        />
      </Cell>
      <Cell $col={2} $row={1}>
        <Waste
          cards={state.waste}
          visibleCount={wasteVisibleCount}
          onClick={onWasteClick}
          selected={wasteSelected}
          sourceTag="w"
        />
      </Cell>
      {state.foundations.map((f, i) => {
        // An empty foundation with nothing selected has no useful click
        // behavior — don't make it look like a button. It becomes clickable
        // again the moment a selection exists (as a drop target for an ace).
        const clickable = f.length > 0 || selection != null;
        return (
          <Cell
            key={i}
            $col={4 + i}
            $row={1}
            data-drop={interactive ? `f:${i}` : undefined}
            ref={
              foundationCellRefs
                ? (el) => {
                    // Braced body: a returned value would read as a
                    // React 19 ref cleanup function.
                    foundationCellRefs.current[i] = el;
                  }
                : undefined
            }
          >
            <Foundation
              stack={f}
              onClick={clickable ? () => onFoundationClick(i) : undefined}
              selected={isSelected(PILE.FOUNDATION, i)}
              sourceTag={`f:${i}`}
            />
          </Cell>
        );
      })}
      {state.tableau.map((col, i) => {
        const selectedFromDepth = isSelected(PILE.TABLEAU, i)
          ? selection.depth
          : null;
        // Empty tableau columns are destination-only — pass onEmptyClick
        // only while a selection exists so the click area (and its cursor
        // pointer) appear only in drop mode.
        const emptyHandler =
          selection != null ? () => onTableauEmptyClick(i) : undefined;
        return (
          <Cell key={i} $col={i + 1} $row={2} data-drop={interactive ? `t:${i}` : undefined}>
            <TableauStack
              faceDown={col.faceDown}
              faceUp={col.faceUp}
              cardBack={cardBack}
              onCardClick={(depth) => onTableauCardClick(i, depth)}
              onEmptyClick={emptyHandler}
              selectedFromDepth={selectedFromDepth}
              sourceTag={`t:${i}`}
            />
          </Cell>
        );
      })}
    </Grid>
  );
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, ${DEFAULT_TILE_WIDTH}px);
  grid-auto-rows: min-content;
  justify-content: space-between;
  gap: ${VERTICAL_GAP}px ${HORIZONTAL_GAP}px;
  align-content: start;
  padding: 6px 12px;
  height: 100%;
  width: 100%;
  background-color: var(--felt-green);
  border-top: 2px solid var(--border-interior-color);
  border-left: 2px solid var(--border-interior-color);
  overflow: hidden;
`;

const Cell = styled.div`
  grid-column: ${({ $col }) => $col};
  grid-row: ${({ $row }) => $row};
  width: ${DEFAULT_TILE_WIDTH}px;
`;

export default Board;
